import {
  PRODUCT_VIEW_PANE,
  PRODUCT_VIEW_PANE_DEFAULT_DESKTOP_ORDER,
  PRODUCT_VIEW_PANE_DEFAULT_MOBILE_ORDER,
} from '../constants/productPanes.js'

/**
 * Capability guards determine which panes require specific capabilities to be visible.
 * If a capability is false, the pane is force-hidden regardless of settings.
 */
const PANE_CAPABILITY_GUARDS = {
  [PRODUCT_VIEW_PANE.VIEWER]: 'hasViewer',
  [PRODUCT_VIEW_PANE.COMPARTMENTS]: 'hasCompartments',
  [PRODUCT_VIEW_PANE.PRICING]: 'hasPricing',
  [PRODUCT_VIEW_PANE.RESOURCES]: 'hasResources',
  [PRODUCT_VIEW_PANE.MODE_CONTROLLER]: 'hasMultipleModes',
}
/**
 * Normalize pane configurations.
 * Supports { id, order, is_sticky, is_visible } shape from panel settings.
 * `is_visible` defaults to true — omitting it means visible.
 */
function normalizePanes(settingsPanes, defaultPanes) {
  const panes = Array.isArray(settingsPanes) && settingsPanes.length > 0
    ? settingsPanes
    : defaultPanes

  return panes
    .map((pane, index) => ({
      id: typeof pane === 'string' ? pane : pane?.id,
      order: pane?.order !== undefined ? pane.order : index,
      is_sticky: pane?.is_sticky ?? false,
      is_visible: pane?.is_visible ?? true,
    }))
    .sort((a, b) => a.order - b.order)
}

/**
 * Apply capability guards — panes whose required capability is false are marked not visible.
 * Returns new array with is_visible updated, originals unchanged.
 */
function applyCapabilityGuards(panes, capabilities) {
  return panes.map((pane) => {
    const guard = PANE_CAPABILITY_GUARDS[pane.id]
    if (!guard) return pane
    if (capabilities?.[guard] === false) {
      return { ...pane, is_visible: false }
    }
    return pane
  })
}

/**
 * Filter out non-visible panes.
 */
function filterVisible(panes) {
  return panes.filter(pane => pane.is_visible)
}

/**
 * Dedupe panes by ID, preferring earlier occurrences.
 */
function dequPaneIds(panes) {
  const seen = new Set()
  return panes.filter((pane) => {
    if (seen.has(pane.id)) return false
    seen.add(pane.id)
    return true
  })
}

/**
 * buildProductLayout — resolves ProductView pane layout for desktop and mobile.
 *
 * Reads from flat panel settings keys:
 *   unit_sheet_layout_desktop: { panes: [{ id, order, is_sticky, is_visible }, ...] }
 *   unit_sheet_layout_mobile:  { panes: [...] }
 *
 * Falls back to hardcoded defaults if settings not provided.
 * Applies capability guards (force-hide if required capability is false).
 *
 * @param {object} params
 * @param {object|null} [params.unitSheetLayoutDesktop]  — panelSettings.unit_sheet_layout_desktop
 * @param {object|null} [params.unitSheetLayoutMobile]   — panelSettings.unit_sheet_layout_mobile
 * @param {object|null} [params.capabilities]
 * @returns {{desktopPaneOrder: array, mobilePaneOrder: array, stickyPanes: Set, hiddenPanes: Set}}
 */
export function buildProductLayout({
  unitSheetLayoutDesktop = null,
  unitSheetLayoutMobile = null,
  capabilities = null,
} = {}) {
  const desktopNormalized = normalizePanes(
    unitSheetLayoutDesktop?.panes,
    PRODUCT_VIEW_PANE_DEFAULT_DESKTOP_ORDER,
  )

  const mobileNormalized = normalizePanes(
    unitSheetLayoutMobile?.panes,
    PRODUCT_VIEW_PANE_DEFAULT_MOBILE_ORDER,
  )

  // Apply capability guards (may mark panes as not visible)
  const desktopGuarded = applyCapabilityGuards(desktopNormalized, capabilities)
  const mobileGuarded = applyCapabilityGuards(mobileNormalized, capabilities)

  // Filter to visible only, then dedupe
  const desktopFinal = dequPaneIds(filterVisible(desktopGuarded))
  const mobileFinal = dequPaneIds(filterVisible(mobileGuarded))

  // Collect sticky and hidden (not-visible) pane IDs from both breakpoints
  const stickyPanes = new Set()
  const hiddenPanes = new Set()

  for (const pane of [...desktopGuarded, ...mobileGuarded]) {
    if (pane.is_sticky) stickyPanes.add(pane.id)
    if (!pane.is_visible) hiddenPanes.add(pane.id)
  }

  return {
    desktopPaneOrder: desktopFinal,
    mobilePaneOrder: mobileFinal,
    stickyPanes,
    hiddenPanes,
  }
}
